<template>
  <div>
    <!-- Wizard form -->
    <b-card
      no-body
      class="mb-2"
    >
      <b-card-header class="d-flex align-items-center">
        <feather-icon
          icon="ZapIcon"
          size="22"
          class="text-warning mr-1"
        />
        <h4 class="mb-0">
          Quick Gain
        </h4>
        <small class="text-muted ml-2">Hızlı kazanç fırsatı bul — TP/SL/Kaldıraç gir, sistem en uygun coin'leri önersin</small>
      </b-card-header>
      <b-card-body>
        <b-row>
          <b-col
            cols="6"
            md="3"
            class="mb-1"
          >
            <label class="font-small-3 text-muted">TP %</label>
            <b-form-input
              v-model.number="form.tpPerc"
              type="number"
              step="0.1"
              min="0.1"
            />
          </b-col>
          <b-col
            cols="6"
            md="3"
            class="mb-1"
          >
            <label class="font-small-3 text-muted">SL %</label>
            <b-form-input
              v-model.number="form.slPerc"
              type="number"
              step="0.1"
              min="0.1"
            />
          </b-col>
          <b-col
            cols="6"
            md="3"
            class="mb-1"
          >
            <label class="font-small-3 text-muted">Kaldıraç</label>
            <b-form-input
              v-model.number="form.leverage"
              type="number"
              step="1"
              min="1"
            />
          </b-col>
          <b-col
            cols="6"
            md="3"
            class="mb-1"
          >
            <label class="font-small-3 text-muted">Komisyon/Yön %</label>
            <b-form-input
              v-model.number="form.commissionPerSide"
              type="number"
              step="0.01"
              min="0"
            />
          </b-col>
          <b-col
            cols="12"
            md="3"
            class="mb-1"
          >
            <label class="font-small-3 text-muted">Market</label>
            <b-form-select
              v-model="form.market"
              :options="marketOptions"
            />
          </b-col>
          <b-col
            cols="12"
            md="3"
            class="mb-1"
          >
            <label class="font-small-3 text-muted">Timeframe'ler (virgüllü)</label>
            <b-form-input
              v-model="form.tfsRaw"
              placeholder="5m,15m,1h"
            />
          </b-col>
          <b-col
            cols="6"
            md="2"
            class="mb-1"
          >
            <label class="font-small-3 text-muted">Top-N Coin</label>
            <b-form-input
              v-model.number="form.topNCoinsFromDb"
              type="number"
              step="1"
              min="1"
              max="200"
            />
          </b-col>
          <b-col
            cols="6"
            md="2"
            class="mb-1"
          >
            <label class="font-small-3 text-muted">Aktif Top-N</label>
            <b-form-input
              v-model.number="form.topNResults"
              type="number"
              step="1"
              min="1"
              max="50"
            />
          </b-col>
          <b-col
            cols="12"
            md="2"
            class="d-flex align-items-end mb-1"
          >
            <b-button
              variant="warning"
              block
              :disabled="loading"
              @click="onRun"
            >
              <feather-icon icon="PlayIcon" /> <strong>Fırsat Ara</strong>
            </b-button>
          </b-col>
        </b-row>

        <!-- Auto-rescan action buttons -->
        <b-row class="mt-1">
          <b-col cols="12">
            <b-button
              variant="outline-primary"
              size="sm"
              @click="onSaveAutoRescan"
            >
              <feather-icon icon="SaveIcon" /> Otomatik taramaya kaydet
            </b-button>
            <b-button
              v-if="rescanDefaults"
              variant="outline-info"
              size="sm"
              class="ml-1"
              @click="onEditRescanDefaults"
            >
              <feather-icon icon="Edit2Icon" /> Kayıtlı ayarı düzenle
            </b-button>
            <b-button
              variant="outline-secondary"
              size="sm"
              class="ml-1"
              @click="onShowRescanStatus"
            >
              <feather-icon icon="ClockIcon" /> Auto-rescan durumu
            </b-button>
          </b-col>
        </b-row>

        <!-- Auto-rescan status panel -->
        <b-alert
          v-if="rescanDefaults"
          :variant="rescanDefaults.enabled ? 'success' : 'secondary'"
          show
          class="mt-1 mb-0"
        >
          <div class="d-flex align-items-center">
            <feather-icon
              :icon="rescanDefaults.enabled ? 'CheckCircleIcon' : 'PauseCircleIcon'"
              size="18"
              class="mr-1"
            />
            <div class="flex-grow-1">
              <strong>Auto-rescan {{ rescanDefaults.enabled ? 'AKTİF' : 'KAPALI' }}</strong>
              <small class="d-block">
                Son çalışma:
                <span v-if="rescanDefaults.lastRunAt">{{ formatDateTime(rescanDefaults.lastRunAt) }}</span>
                <span v-else>henüz çalışmadı</span>
                <span v-if="rescanDefaults.lastRunSummary">
                  · {{ rescanDefaults.lastRunSummary.passed }}/{{ rescanDefaults.lastRunSummary.total }} geçti
                </span>
              </small>
              <small class="d-block text-muted">
                Kayıt: TP {{ formatNum(rescanDefaults.tpPerc) }} / SL {{ formatNum(rescanDefaults.slPerc) }} ·
                {{ savedRescanTimeframes }} · Top-N {{ rescanDefaults.topNCoinsFromDb || '—' }} / {{ rescanDefaults.topNResults || '—' }}
              </small>
              <small class="d-block text-muted">
                Fallback: {{ opportunityFallbackModeLabel(savedOpportunityFallback.mode) }} ·
                min strict {{ savedOpportunityFallback.minStrictActiveEdges }} ·
                probation {{ savedOpportunityFallback.maxProbationActive }}
              </small>
              <small class="d-block text-muted">
                Pool vites: {{ poolGearModeLabel(savedPoolGear.mode) }}
                <span v-if="lastPoolGearSummary">
                  · aktif {{ lastPoolGearSummary.gear }} · {{ lastPoolGearSummary.reason }}
                </span>
              </small>
            </div>
            <b-button
              :variant="rescanDefaults.enabled ? 'outline-warning' : 'outline-success'"
              size="sm"
              :disabled="triggeringRescan"
              @click="onToggleRescan"
            >
              {{ rescanDefaults.enabled ? 'Pasif Yap' : 'Aktive Et' }}
            </b-button>
            <b-button
              v-if="rescanDefaults.enabled"
              variant="outline-primary"
              size="sm"
              class="ml-1"
              :disabled="triggeringRescan"
              @click="onTriggerRescan"
            >
              <b-spinner
                v-if="triggeringRescan"
                small
              />
              <feather-icon
                v-else
                icon="RefreshCwIcon"
              />
              Tetikle
            </b-button>
            <b-button
              variant="outline-danger"
              size="sm"
              class="ml-1"
              @click="onRemoveRescan"
            >
              <feather-icon icon="Trash2Icon" /> Sil
            </b-button>
          </div>
        </b-alert>

        <b-alert
          v-if="rescanDefaults"
          :variant="opportunityPanelTone"
          show
          class="mt-1 mb-0"
        >
          <div class="d-flex align-items-start">
            <feather-icon
              :icon="opportunityPanelIcon"
              size="18"
              class="mr-1 mt-25"
            />
            <div class="flex-grow-1">
              <div class="d-flex flex-wrap align-items-center">
                <strong class="mr-1">{{ opportunityPanelHeadline }}</strong>
                <b-badge
                  :variant="opportunityModeBadgeVariant(savedOpportunityFallback.mode)"
                  class="mr-50"
                >
                  {{ opportunityFallbackModeLabel(savedOpportunityFallback.mode) }}
                </b-badge>
                <b-badge
                  v-if="lastFallbackSummary"
                  variant="light-secondary"
                  class="mr-50"
                >
                  strict {{ lastFallbackSummary.strictPassed }}/{{ lastFallbackSummary.minStrictActiveEdges }}
                </b-badge>
                <b-badge
                  v-if="lastFallbackApply"
                  variant="light-info"
                >
                  probation +{{ lastFallbackApply.activated.length || 0 }} / -{{ lastFallbackApply.deactivated.length || 0 }}
                </b-badge>
              </div>
              <small class="d-block mt-50">
                {{ opportunityPanelDetail }}
              </small>
              <div
                v-if="opportunityCandidateRows.length"
                class="opportunity-candidate-grid mt-1"
              >
                <div
                  v-for="candidate in opportunityCandidateRows"
                  :key="`${candidate.symbol}-${candidate.timeframe}-${candidate.indicatorId}`"
                  class="opportunity-candidate-row"
                >
                  <div>
                    <strong>{{ candidate.symbol }}</strong>
                    <small class="d-block text-muted">{{ candidate.timeframe }} · {{ candidate.engineKey }}</small>
                  </div>
                  <b-badge variant="light-primary">
                    skor {{ formatNum(candidate.opportunityScore, 2) }}
                  </b-badge>
                  <small class="text-muted">
                    EV {{ formatNum(candidate.metrics.netEvPerTrade, 3) }} ·
                    son TP {{ candidate.metrics.recentTpCount }}/{{ candidate.metrics.recentTotalCount }}
                  </small>
                  <small class="text-muted">
                    {{ opportunityCandidateReason(candidate) }}
                  </small>
                </div>
              </div>
              <div
                v-if="opportunityRuntimeRows.length"
                class="opportunity-runtime-grid mt-1"
              >
                <div
                  v-for="edge in opportunityRuntimeRows"
                  :key="edge.id"
                  class="opportunity-runtime-row"
                >
                  <div>
                    <strong>{{ edge.symbol }}</strong>
                    <small class="d-block text-muted">{{ edge.timeframe }} · edge #{{ edge.id }}</small>
                  </div>
                  <b-badge :variant="edge.badgeVariant">
                    {{ edge.tierLabel }}
                  </b-badge>
                  <small class="text-muted">
                    {{ edge.statsLabel }}
                  </small>
                  <small :class="edge.lastOutcomeClass">
                    {{ edge.lastOutcomeLabel }}
                  </small>
                </div>
              </div>
            </div>
          </div>
        </b-alert>

        <b-alert
          v-if="rescanDefaults"
          variant="light-primary"
          show
          class="mt-1 mb-0"
        >
          <b-row class="align-items-center">
            <b-col
              cols="12"
              md="4"
              class="mb-1 mb-md-0"
            >
              <label class="font-small-3 text-muted">TP/SL uyarlama</label>
              <b-form-select
                v-model="form.tpSlTuningMode"
                :options="tpSlTuningOptions"
              />
              <small
                v-if="tpSlModeRecommendation"
                class="text-muted d-block mt-50"
              >
                Önerilen mod: <strong>{{ tpSlModeRecommendationLabel }}</strong> · {{ tpSlModeRecommendation.reason }}
              </small>
              <b-button
                v-if="tpSlModeRecommendation && form.tpSlTuningMode !== tpSlModeRecommendation.mode"
                variant="outline-primary"
                size="sm"
                class="mt-50"
                @click="onApplyTpSlModeRecommendation"
              >
                Önerilen modu kullan
              </b-button>
              <small class="text-muted d-block mt-50">
                Öneriler işlem sayısı, net EV, toplam sonuç, profit factor ve drawdown birlikte değerlendirilerek hazırlanır.
              </small>
            </b-col>
            <b-col
              v-if="tpSlTuning"
              cols="12"
              md="8"
            >
              <div class="d-flex flex-wrap align-items-start">
                <div class="mr-2 flex-grow-1">
                  <strong>{{ tpSlTuningStatus }}</strong>
                  <small class="d-block text-muted">
                    Mevcut {{ formatNum(tpSlTuning.current && tpSlTuning.current.tpPerc) }} /
                    {{ formatNum(tpSlTuning.current && tpSlTuning.current.slPerc) }}
                    <span v-if="tpSlRecommendation">
                      · Öneri {{ formatNum(tpSlRecommendation.tpPerc) }} /
                      {{ formatNum(tpSlRecommendation.slPerc) }}
                    </span>
                  </small>
                  <small
                    v-if="tpSlRecommendation"
                    class="d-block text-muted"
                  >
                    {{ tpSlRecommendation.trades }} işlem · EV {{ formatNum(tpSlRecommendation.netEv, 3) }} ·
                    PF {{ formatNum(tpSlRecommendation.profitFactor, 2) }} ·
                    veri kapsamı %{{ formatNum((tpSlRecommendation.sampleCoverage || 0) * 100, 0) }} ·
                    iyileşme %{{ formatNum(tpSlRecommendation.improvementPct, 1) }} ·
                    kararlılık {{ tpSlTuning.stableRuns || 0 }}/2
                  </small>
                  <small
                    v-if="tpSlRecommendationWarning"
                    class="d-block text-warning"
                  >
                    {{ tpSlRecommendationWarning }}
                  </small>
                  <b-alert
                    v-if="tpSlApplyLifecycle"
                    :variant="tpSlLifecycleTone"
                    show
                    class="mt-1 mb-0 py-50 px-1"
                  >
                    <strong>{{ tpSlLifecycleHeadline }}</strong>
                    <small class="d-block mt-50">
                      {{ tpSlLifecycleDetail }}
                    </small>
                  </b-alert>
                  <small
                    v-if="tpSlCarrySummary"
                    class="d-block text-muted"
                  >
                    Son {{ tpSlCarrySummary.sampleCount }} finalize sinyal: signal MFE {{ formatNum(tpSlCarrySummary.avgSignalMfe, 2) }} /
                    MAE {{ formatNum(tpSlCarrySummary.avgSignalMae, 2) }} · decision MFE {{ formatNum(tpSlCarrySummary.avgDecisionMfe, 2) }} ·
                    mevcut TP erişim %{{ formatNum(tpSlCarrySummary.signalTpReachRate, 0) }} ·
                    decision TP erişim %{{ formatNum(tpSlCarrySummary.decisionTpReachRate, 0) }} ·
                    SL izi %{{ formatNum(tpSlCarrySummary.signalSlReachRate, 0) }} ·
                    ilk hit TP %{{ formatNum(tpSlCarrySummary.tpFirstRate, 0) }}
                  </small>
                  <small
                    v-if="tpSlScanContext"
                    class="d-block text-muted"
                  >
                    Scan context: {{ tpSlScanContext.coveredIndicatorCount }}/{{ tpSlScanContext.indicatorCount }} indikatör ·
                    destek {{ tpSlScanContext.supportiveIndicatorCount }} / zayıf {{ tpSlScanContext.weakIndicatorCount }} ·
                    W {{ formatNum(tpSlScanContext.avgGuidanceWeight, 2) }} ·
                    ΔS {{ formatSigned(tpSlScanContext.avgGuidanceDelta, 2) }} ·
                    rejim {{ tpSlScanContext.dominantRegimeLabel }} %{{ formatNum(tpSlScanContext.dominantRegimeShare, 0) }}
                    <span v-if="tpSlScanContextLabel">
                      · {{ tpSlScanContextLabel }}
                    </span>
                  </small>
                  <small
                    v-if="tpSlEligibilityText"
                    class="d-block text-muted"
                  >
                    Entry eligibility: <strong>{{ tpSlEligibilityText }}</strong>
                  </small>
                  <b-alert
                    v-if="tpSlApplyAdvisory && tpSlApplyAdvisory.level !== 'CLEAR'"
                    :variant="tpSlApplyAdvisoryTone"
                    show
                    class="mt-1 mb-0 py-50 px-1"
                  >
                    <strong>{{ tpSlApplyAdvisoryHeadline }}</strong>
                    <small class="d-block mt-50">
                      <span
                        v-if="tpSlApplyAdvisoryReasonCode"
                        class="d-block text-uppercase"
                      >
                        {{ tpSlApplyAdvisoryReasonCode }}
                      </span>
                      {{ tpSlApplyAdvisoryDetail }}
                    </small>
                  </b-alert>
                  <div
                    v-if="tpSlComparisonRows.length"
                    class="tp-sl-compare-panel mt-1"
                  >
                    <div class="tp-sl-compare-header">
                      <span />
                      <span>Mevcut</span>
                      <span>Öneri</span>
                      <span>Delta</span>
                    </div>
                    <div
                      v-for="row in tpSlComparisonRows"
                      :key="row.key"
                      class="tp-sl-compare-row"
                    >
                      <span class="text-muted">{{ row.label }}</span>
                      <strong>{{ row.current }}</strong>
                      <strong class="text-primary">{{ row.recommended }}</strong>
                      <b-badge :variant="row.variant">
                        {{ row.delta }}
                      </b-badge>
                    </div>
                    <div
                      v-if="tpSlRecommendationDrivers.length"
                      class="tp-sl-driver-row"
                    >
                      <small class="text-muted">Neden:</small>
                      <span
                        v-for="driver in tpSlRecommendationDrivers"
                        :key="driver.label"
                        class="tp-sl-driver-chip"
                      >
                        {{ driver.label }} {{ driver.delta }}
                      </span>
                    </div>
                  </div>
                </div>
                <b-button
                  v-if="tpSlRecommendation"
                  variant="primary"
                  size="sm"
                  class="mt-50 mt-md-0"
                  :disabled="applyingTpSl"
                  @click="onApplyTpSlRecommendation"
                >
                  <b-spinner
                    v-if="applyingTpSl"
                    small
                  />
                  <feather-icon
                    v-else
                    icon="CheckIcon"
                  />
                  Öneriyi Uygula
                </b-button>
              </div>
            </b-col>
          </b-row>
          <small
            v-if="form.tpSlTuningMode === 'AUTO'"
            class="text-warning d-block mt-1"
          >
            Otomatik mod yalnız en az 50 işlem, iki ardışık taramada aynı öneri, en az %15 puan iyileşmesi ve 20 dakika bekleme şartlarıyla değişiklik yapar. Açık pozisyonlar değiştirilmez.
          </small>
        </b-alert>

        <!-- Advanced (collapsable) -->
        <b-row>
          <b-col cols="12">
            <b-button
              variant="link"
              size="sm"
              @click="advancedOpen = !advancedOpen"
            >
              <feather-icon :icon="advancedOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'" />
              {{ advancedOpen ? 'Gelişmiş ayarları gizle' : 'Gelişmiş ayarlar' }}
            </b-button>
            <small class="text-muted ml-1">{{ selectedIndicatorSummary }}</small>
          </b-col>
        </b-row>
        <b-collapse v-model="advancedOpen">
          <b-row class="mt-1">
            <b-col
              cols="12"
              md="4"
              class="mb-1"
            >
              <label class="font-small-3 text-muted">Spesifik Coin'ler (virgüllü, opsiyonel)</label>
              <b-form-input
                v-model="form.coinsRaw"
                placeholder="Boş bırak → DB'den top-N alınır"
              />
            </b-col>
            <b-col
              cols="6"
              md="2"
              class="mb-1"
            >
              <label class="font-small-3 text-muted">Min Trade</label>
              <b-form-input
                v-model.number="form.minTrades"
                type="number"
                step="1"
                min="0"
              />
            </b-col>
            <b-col
              cols="6"
              md="2"
              class="mb-1"
            >
              <label class="font-small-3 text-muted">Min Net EV %</label>
              <b-form-input
                v-model.number="form.minNetEv"
                type="number"
                step="0.01"
              />
            </b-col>
            <b-col
              cols="6"
              md="2"
              class="mb-1"
            >
              <label class="font-small-3 text-muted">Min Win Rate %</label>
              <b-form-input
                v-model.number="form.minWinRate"
                type="number"
                step="1"
                min="0"
                max="100"
              />
            </b-col>
            <b-col
              cols="6"
              md="2"
              class="mb-1"
            >
              <label class="font-small-3 text-muted">Mum sayısı</label>
              <b-form-input
                v-model.number="form.candleLimit"
                type="number"
                step="100"
                min="100"
                max="1500"
              />
            </b-col>

            <b-col cols="12">
              <hr class="my-1">
              <div class="d-flex align-items-center justify-content-between mb-1">
                <div>
                  <strong>Pool vites yönetimi</strong>
                  <small class="text-muted d-block">
                    Approved 3Gear aciksa rescan, son kapanan islemlere gore soft/relaxed/zero filtrelerini otomatik secer.
                  </small>
                </div>
              </div>
              <b-row>
                <b-col
                  cols="12"
                  md="3"
                  class="mb-1"
                >
                  <label class="font-small-3 text-muted">Mod</label>
                  <b-form-select
                    v-model="form.quickGainPoolGear.mode"
                    :options="poolGearOptions"
                  />
                </b-col>
                <b-col
                  cols="6"
                  md="2"
                  class="mb-1"
                >
                  <label class="font-small-3 text-muted">Başlangıç vitesi</label>
                  <b-form-input
                    v-model.number="form.quickGainPoolGear.startGear"
                    type="number"
                    min="1"
                    max="3"
                    step="1"
                    :disabled="form.quickGainPoolGear.mode === 'OFF'"
                  />
                </b-col>
                <b-col
                  cols="6"
                  md="2"
                  class="mb-1"
                >
                  <label class="font-small-3 text-muted">Lookback trade</label>
                  <b-form-input
                    v-model.number="form.quickGainPoolGear.lookbackTrades"
                    type="number"
                    min="1"
                    max="100"
                    step="1"
                    :disabled="form.quickGainPoolGear.mode === 'OFF'"
                  />
                </b-col>
                <b-col
                  cols="6"
                  md="2"
                  class="mb-1"
                >
                  <label class="font-small-3 text-muted">Min trade</label>
                  <b-form-input
                    v-model.number="form.quickGainPoolGear.minTrades"
                    type="number"
                    min="1"
                    max="100"
                    step="1"
                    :disabled="form.quickGainPoolGear.mode === 'OFF'"
                  />
                </b-col>
                <b-col
                  cols="6"
                  md="3"
                  class="mb-1"
                >
                  <label class="font-small-3 text-muted">Cooldown dk</label>
                  <b-form-input
                    v-model.number="form.quickGainPoolGear.cooldownMinutes"
                    type="number"
                    min="0"
                    max="1440"
                    step="5"
                    :disabled="form.quickGainPoolGear.mode === 'OFF'"
                  />
                </b-col>
                <b-col
                  cols="6"
                  md="3"
                  class="mb-1"
                >
                  <label class="font-small-3 text-muted">Düşürme net USDT</label>
                  <b-form-input
                    v-model.number="form.quickGainPoolGear.lossThresholdUsdt"
                    type="number"
                    max="0"
                    step="10"
                    :disabled="form.quickGainPoolGear.mode === 'OFF'"
                  />
                </b-col>
                <b-col
                  cols="6"
                  md="3"
                  class="mb-1"
                >
                  <label class="font-small-3 text-muted">Düşürme max WR %</label>
                  <b-form-input
                    v-model.number="form.quickGainPoolGear.maxWinRate"
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    :disabled="form.quickGainPoolGear.mode === 'OFF'"
                  />
                </b-col>
                <b-col
                  cols="6"
                  md="3"
                  class="mb-1"
                >
                  <label class="font-small-3 text-muted">Yükseltme net USDT</label>
                  <b-form-input
                    v-model.number="form.quickGainPoolGear.upProfitThresholdUsdt"
                    type="number"
                    min="0"
                    step="10"
                    :disabled="form.quickGainPoolGear.mode === 'OFF'"
                  />
                </b-col>
                <b-col
                  cols="6"
                  md="3"
                  class="mb-1"
                >
                  <label class="font-small-3 text-muted">Yükseltme min WR %</label>
                  <b-form-input
                    v-model.number="form.quickGainPoolGear.upMinWinRate"
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    :disabled="form.quickGainPoolGear.mode === 'OFF'"
                  />
                </b-col>
              </b-row>
              <div
                v-if="form.quickGainPoolGear.mode === 'APPROVED_3_SPEED'"
                class="pool-gear-preset-panel mt-50"
              >
                <div class="d-flex flex-wrap align-items-center justify-content-between mb-50">
                  <small class="text-muted">
                    Approved preset degerleri
                  </small>
                  <b-badge variant="light-primary">
                    aktif vites {{ lastPoolGearSummary ? lastPoolGearSummary.gear : form.quickGainPoolGear.startGear }}
                  </b-badge>
                </div>
                <b-table
                  small
                  responsive
                  hover
                  :items="poolGearPresetRows"
                  :fields="poolGearPresetFields"
                  class="mb-0 quick-gain-compact-table"
                >
                  <template #cell(gear)="data">
                    <strong>{{ data.item.gear }}</strong>
                    <small class="d-block text-muted">{{ data.item.label }}</small>
                  </template>
                  <template #cell(maxEdges)="data">
                    <b-badge variant="light-info">
                      {{ data.item.maxEdges }}
                    </b-badge>
                  </template>
                  <template #cell(minScoreFinal)="data">
                    {{ formatNum(data.item.minScoreFinal, 2) }}
                  </template>
                  <template #cell(minWinRate)="data">
                    %{{ formatNum(data.item.minWinRate, 0) }}
                  </template>
                  <template #cell(minNetEv)="data">
                    {{ formatNum(data.item.minNetEv, 2) }}
                  </template>
                  <template #cell(rejectLastExitSl)="data">
                    {{ data.item.rejectLastExitSl ? 'Acik' : 'Kapali' }}
                  </template>
                </b-table>
              </div>
            </b-col>

            <b-col cols="12">
              <hr class="my-1">
              <div class="d-flex align-items-center justify-content-between mb-1">
                <div>
                  <strong>Opportunity fallback</strong>
                  <small class="text-muted d-block">
                    Rescan strict sonuç bulamazsa aynı kapsamda düşük riskli fırsat adaylarını otomatik arar.
                  </small>
                </div>
              </div>
              <b-row>
                <b-col
                  cols="12"
                  md="3"
                  class="mb-1"
                >
                  <label class="font-small-3 text-muted">Mod</label>
                  <b-form-select
                    v-model="form.quickGainOpportunityFallback.mode"
                    :options="opportunityFallbackOptions"
                  />
                </b-col>
                <b-col
                  cols="6"
                  md="2"
                  class="mb-1"
                >
                  <label class="font-small-3 text-muted">Min strict edge</label>
                  <b-form-input
                    v-model.number="form.quickGainOpportunityFallback.minStrictActiveEdges"
                    type="number"
                    min="0"
                    max="50"
                    step="1"
                  />
                </b-col>
                <b-col
                  cols="6"
                  md="2"
                  class="mb-1"
                >
                  <label class="font-small-3 text-muted">Max aday</label>
                  <b-form-input
                    v-model.number="form.quickGainOpportunityFallback.maxCandidates"
                    type="number"
                    min="1"
                    max="100"
                    step="1"
                  />
                </b-col>
                <b-col
                  cols="6"
                  md="2"
                  class="mb-1"
                >
                  <label class="font-small-3 text-muted">Max probation</label>
                  <b-form-input
                    v-model.number="form.quickGainOpportunityFallback.maxProbationActive"
                    type="number"
                    min="0"
                    max="50"
                    step="1"
                  />
                </b-col>
                <b-col
                  cols="6"
                  md="3"
                  class="mb-1"
                >
                  <label class="font-small-3 text-muted">Risk çarpanı</label>
                  <b-form-input
                    v-model.number="form.quickGainOpportunityFallback.allocationMultiplier"
                    type="number"
                    min="0"
                    max="1"
                    step="0.05"
                  />
                </b-col>
              </b-row>
            </b-col>

            <!-- ─── Momentum filter (son N işlem TP olmalı) ─── -->
            <b-col cols="12">
              <hr class="my-1">
              <div class="d-flex align-items-center justify-content-between mb-1">
                <div>
                  <strong>İndikatör kapsamı</strong>
                  <small class="text-muted d-block">
                    {{ selectedIndicatorSummary }}
                  </small>
                </div>
                <div>
                  <b-button
                    variant="outline-primary"
                    size="sm"
                    class="mr-1"
                    @click="selectAllIndicators"
                  >
                    Tümü
                  </b-button>
                  <b-button
                    variant="outline-secondary"
                    size="sm"
                    @click="clearIndicatorSelection"
                  >
                    Temizle
                  </b-button>
                </div>
              </div>
              <b-row>
                <b-col
                  v-for="indicator in indicatorListForSelection"
                  :key="indicator.id"
                  cols="12"
                  md="6"
                  lg="4"
                  class="mb-50"
                >
                  <b-form-checkbox
                    :checked="isIndicatorSelected(indicator.id)"
                    @change="toggleIndicatorSelection(indicator.id, $event)"
                  >
                    <span class="font-weight-bold">{{ indicator.name }}</span>
                    <small class="text-muted d-block">{{ indicator.engineKey }}</small>
                  </b-form-checkbox>
                </b-col>
              </b-row>
              <b-alert
                v-if="!indicatorListForSelection.length"
                variant="warning"
                show
                class="mt-1 mb-0"
              >
                Aktif indikatör bulunamadı. Önce indikatörleri sync et veya aktif bırak.
              </b-alert>
            </b-col>

            <b-col cols="12">
              <hr class="my-1">
              <div class="d-flex align-items-center mb-1">
                <b-form-checkbox
                  v-model="form.requireRecentSuccess"
                  switch
                >
                  <strong>🔥 Momentum filtresi:</strong> Son N işlemin tümü TP olmalı
                </b-form-checkbox>
                <small class="text-muted ml-2">(K/Z + olmasa da olur, sadece son trade'leri başarılı setup'ları döner)</small>
              </div>
              <b-row v-if="form.requireRecentSuccess">
                <b-col
                  cols="6"
                  md="3"
                  class="mb-1"
                >
                  <label class="font-small-3 text-muted">Son kaç işlem kontrol edilsin?</label>
                  <b-form-input
                    v-model.number="form.recentTradeCount"
                    type="number"
                    step="1"
                    min="1"
                    max="20"
                  />
                </b-col>
                <b-col
                  cols="12"
                  md="9"
                  class="d-flex align-items-end mb-1"
                >
                  <small class="text-info">
                    <feather-icon icon="InfoIcon" />
                    Bu filtre aktifken yalnız son {{ form.recentTradeCount }} işlemin <strong>hepsi TP</strong> ile bitmiş edge'ler önerilir.
                  </small>
                </b-col>
              </b-row>
            </b-col>

            <!-- ─── Recency priority (skor multiplier) ─── -->
            <b-col cols="12">
              <hr class="my-1">
              <b-form-checkbox
                v-model="form.prioritizeRecent"
                switch
              >
                <strong>📈 En güncel hareket yapan coinleri öne çıkar</strong>
              </b-form-checkbox>
              <small class="text-muted d-block ml-3">
                Skor çarpanı: son trade ≤5 bar = ×1.5, ≤20 bar = ×1.3, ≤50 bar = ×1.15. Volatilite son 20/önceki 20 bar oranı ≥1.5 = ×1.3.
                Aynı şartları taşıyan ama daha güncel hareket eden coinler üst sıraya çıkar.
              </small>
            </b-col>
          </b-row>
        </b-collapse>
      </b-card-body>
    </b-card>

    <!-- Active indicator info -->
    <b-alert
      v-if="result && config"
      variant="info"
      show
      class="mb-2"
    >
      <feather-icon icon="InfoIcon" />
      <strong>Tarama İndikatörü:</strong> {{ config.indicatorName }} ({{ config.engineKey }}) ·
      <span v-if="config.indicatorIds && config.indicatorIds.length > 1">
        {{ config.indicatorIds.length }} indikatör ·
      </span>
      <strong>{{ config.coins.length }} coin × {{ config.timeframes.length }} TF</strong>
      taranıyor · TP {{ config.tpPerc }}% / SL {{ config.slPerc }}% / Lev {{ config.leverage }}x
    </b-alert>

    <!-- Loading -->
    <div
      v-if="loading"
      class="text-center py-4"
    >
      <b-spinner
        large
        variant="warning"
      />
      <h5 class="mt-2 text-muted">
        Fırsatlar aranıyor...
      </h5>
      <small class="text-muted">Coin × TF kombinasyonları için backtest yapılıyor — birkaç saniye sürebilir</small>
    </div>

    <!-- Results: card grid -->
    <div v-if="topResults.length > 0 && !loading">
      <h4 class="mb-2">
        🎯 {{ topResults.length }} fırsat bulundu
        <small class="text-muted">— skor sırasıyla</small>
      </h4>
      <b-row>
        <b-col
          v-for="(item, idx) in topResults"
          :key="`${item.symbol}-${item.timeframe}-${item.indicatorId || item.engineKey || idx}`"
          cols="12"
          md="6"
          lg="4"
          class="mb-2"
        >
          <b-card
            no-body
            class="border-success-light h-100"
          >
            <b-card-header class="d-flex align-items-center pb-1">
              <div class="d-flex align-items-center flex-grow-1">
                <b-badge
                  variant="primary"
                  class="mr-1"
                >
                  #{{ idx + 1 }}
                </b-badge>
                <div>
                  <h5 class="mb-0">
                    {{ item.symbol }}
                  </h5>
                  <small class="text-muted">{{ item.timeframe }} · {{ item.engineKey }}</small>
                </div>
              </div>
              <b-badge variant="warning">
                QG
              </b-badge>
            </b-card-header>
            <b-card-body class="pt-0">
              <b-row class="text-center mb-1">
                <b-col
                  cols="4"
                  class="border-right"
                >
                  <small class="text-muted">Skor</small>
                  <h4
                    :class="item.scoreFinal >= 0 ? 'text-success' : 'text-danger'"
                    class="mb-0"
                  >
                    {{ formatNum(item.scoreFinal, 3) }}
                  </h4>
                </b-col>
                <b-col
                  cols="4"
                  class="border-right"
                >
                  <small class="text-muted">Win Rate</small>
                  <h4
                    :class="(item.winRate || 0) >= 50 ? 'text-success' : 'text-warning'"
                    class="mb-0"
                  >
                    {{ formatNum(item.winRate, 1) }}%
                  </h4>
                </b-col>
                <b-col cols="4">
                  <small class="text-muted">Net EV</small>
                  <h4
                    :class="item.netEvPerTrade >= 0 ? 'text-success' : 'text-danger'"
                    class="mb-0"
                  >
                    {{ formatNum(item.netEvPerTrade, 3) }}%
                  </h4>
                </b-col>
              </b-row>
              <div class="d-flex justify-content-between font-small-3 text-muted mb-1">
                <span>📊 {{ item.exits }} trade</span>
                <span>📉 DD {{ formatNum(item.maxDrawdown, 2) }}%</span>
              </div>
              <!-- Son N işlemde kaç TP -->
              <div
                v-if="item.recentTotalCount"
                class="text-center mb-1"
              >
                <b-badge
                  :variant="recentBadgeVariant(item)"
                  class="font-small-3"
                >
                  🔥 Son {{ item.recentTotalCount }} işlem: {{ item.recentTpCount }}/{{ item.recentTotalCount }} TP
                </b-badge>
              </div>
              <!-- Recency: güncellik info -->
              <div
                v-if="item.barsFromLastTrade !== undefined && item.barsFromLastTrade !== null"
                class="text-center mb-1"
              >
                <b-badge
                  variant="light-info"
                  class="font-small-3"
                  :title="recencyTooltip(item)"
                >
                  📈 Son trade {{ item.barsFromLastTrade }} bar önce
                  <span
                    v-if="item.recencyMul && item.recencyMul !== 1"
                    :class="item.recencyMul > 1 ? 'text-success' : 'text-warning'"
                  >
                    · ×{{ formatNum(item.recencyMul, 2) }}
                  </span>
                </b-badge>
              </div>
              <hr class="my-1">
              <div class="d-flex">
                <b-button
                  variant="outline-info"
                  size="sm"
                  class="flex-grow-1 mr-1"
                  @click="onShowDetail(item)"
                >
                  <feather-icon icon="EyeIcon" /> Detay
                </b-button>
                <b-button
                  :variant="activatedIds.includes(item.edgeId) ? 'success' : 'outline-success'"
                  size="sm"
                  class="flex-grow-1"
                  :disabled="!item.edgeId || activatedIds.includes(item.edgeId)"
                  @click="onActivate(item)"
                >
                  <feather-icon :icon="activatedIds.includes(item.edgeId) ? 'CheckIcon' : 'ZapIcon'" />
                  {{ activatedIds.includes(item.edgeId) ? 'Aktif' : 'Aktive Et' }}
                </b-button>
              </div>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>

      <div class="text-right mt-2">
        <b-button
          variant="outline-primary"
          :to="{ name: 'Edges' }"
        >
          <feather-icon icon="ListIcon" /> Library'de Görüntüle
        </b-button>
      </div>
    </div>

    <!-- Empty state -->
    <b-card
      v-else-if="result && !loading"
      class="text-center py-4"
    >
      <feather-icon
        icon="MehIcon"
        size="48"
        class="text-muted mb-1"
      />
      <h4 class="text-muted">
        Eşiği geçen sonuç yok
      </h4>
      <p class="text-muted">
        {{ summary ? `${summary.total} kombinasyon tarandı, ${summary.passed} geçti.` : '' }}
        TP/SL'i gevşetmeyi veya gelişmiş ayarlarda filtreleri düşürmeyi dene.
      </p>
    </b-card>

    <!-- ─── Detail modal: backtest of selected fırsat ──────────────────────── -->
    <b-modal
      v-model="detailModal.show"
      size="xl"
      hide-footer
      no-close-on-backdrop
      @hide="onCloseDetail"
    >
      <template #modal-title>
        <span v-if="detailModal.item">
          <feather-icon icon="EyeIcon" /> {{ detailModal.item.symbol }} · {{ detailModal.item.timeframe }} · {{ detailModal.item.engineKey }}
        </span>
        <span v-else>Detay</span>
      </template>

      <!-- Item meta header -->
      <b-card
        v-if="detailModal.item"
        no-body
        class="mb-2 bg-light-info"
      >
        <b-card-body class="py-1">
          <b-row class="text-center">
            <b-col
              cols="6"
              md="3"
            >
              <small class="text-muted">Skor</small>
              <h5
                :class="detailModal.item.scoreFinal >= 0 ? 'text-success' : 'text-danger'"
                class="mb-0"
              >
                {{ formatNum(detailModal.item.scoreFinal, 3) }}
              </h5>
            </b-col>
            <b-col
              cols="6"
              md="3"
            >
              <small class="text-muted">Win Rate</small>
              <h5 class="mb-0">
                {{ formatNum(detailModal.item.winRate, 1) }}%
              </h5>
            </b-col>
            <b-col
              cols="6"
              md="3"
            >
              <small class="text-muted">Net EV</small>
              <h5
                :class="detailModal.item.netEvPerTrade >= 0 ? 'text-success' : 'text-danger'"
                class="mb-0"
              >
                {{ formatNum(detailModal.item.netEvPerTrade, 3) }}%
              </h5>
            </b-col>
            <b-col
              cols="6"
              md="3"
            >
              <small class="text-muted">Trade Sayısı</small>
              <h5 class="mb-0">
                {{ detailModal.item.exits }}
              </h5>
            </b-col>
          </b-row>
          <div
            v-if="config"
            class="text-center mt-1 font-small-3 text-muted"
          >
            TP {{ config.tpPerc }}% · SL {{ config.slPerc }}% · Lev {{ config.leverage }}x · Kom/Yön {{ config.commissionPerSide }}%
          </div>
        </b-card-body>
      </b-card>

      <!-- Loading state -->
      <div
        v-if="detailLoading"
        class="text-center py-3"
      >
        <b-spinner /> Backtest çalışıyor — geçmiş işlemler getiriliyor...
      </div>

      <!-- Result -->
      <div v-if="detailResult && !detailLoading">
        <!-- Quality details -->
        <h5>Kalite Özeti</h5>
        <b-row class="mb-2">
          <b-col
            cols="6"
            md="3"
          ><strong>Net PnL:</strong>
            <span :class="(detailResult.quality.netCumulativePnl || 0) >= 0 ? 'text-success' : 'text-danger'">
              {{ formatNum(detailResult.quality.netCumulativePnl, 2) }}%
            </span>
          </b-col>
          <b-col
            cols="6"
            md="3"
          >
            <strong>Gross PnL:</strong> {{ formatNum(detailResult.quality.grossCumulativePnl, 2) }}%
          </b-col>
          <b-col
            cols="6"
            md="3"
          >
            <strong>Avg Win:</strong> {{ formatNum(detailResult.quality.avgProfit, 3) }}%
          </b-col>
          <b-col
            cols="6"
            md="3"
          >
            <strong>Avg Loss:</strong> {{ formatNum(detailResult.quality.avgLoss, 3) }}%
          </b-col>
          <b-col
            cols="6"
            md="3"
          >
            <strong>Break-even WR:</strong> {{ formatNum(detailResult.quality.breakEvenWinRate, 1) }}%
          </b-col>
          <b-col
            cols="6"
            md="3"
          ><strong>WR Gap:</strong>
            <span :class="(detailResult.quality.winRateGap || 0) >= 0 ? 'text-success' : 'text-danger'">
              {{ formatNum(detailResult.quality.winRateGap, 1) }} puan
            </span>
          </b-col>
          <b-col
            cols="6"
            md="3"
          >
            <strong>Komisyon RT:</strong> {{ formatNum(detailResult.quality.commissionRoundTrip, 2) }}%
          </b-col>
          <b-col
            cols="6"
            md="3"
          ><strong>Net EV/Trade:</strong>
            <span :class="(detailResult.quality.netExpectancyPerTrade || 0) >= 0 ? 'text-success' : 'text-danger'">
              {{ formatNum(detailResult.quality.netExpectancyPerTrade, 3) }}%
            </span>
          </b-col>
        </b-row>

        <!-- Trade pairs table -->
        <div class="d-flex align-items-center mb-1">
          <h5 class="mb-0">
            Trade Geçmişi
          </h5>
          <small class="text-muted ml-2">
            (En son {{ Math.min(detailTradePairs.length, 30) }} / toplam {{ detailTradePairs.length }})
          </small>
          <b-form-checkbox
            v-model="showOpenTrades"
            switch
            class="ml-auto"
          >
            Açık pozisyonları da göster
          </b-form-checkbox>
        </div>
        <b-table
          :items="visibleDetailPairs"
          :fields="tradeFields"
          small
          striped
          responsive
          show-empty
          empty-text="Henüz tamamlanmış trade yok."
        >
          <template #cell(idx)="data">
            #{{ data.item.idx }}
          </template>
          <template #cell(side)="data">
            <b-badge :variant="data.item.side === 'LONG' ? 'success' : 'danger'">
              {{ data.item.side }}
            </b-badge>
          </template>
          <template #cell(entryTime)="data">
            <div class="font-small-3">
              {{ formatDateTime(data.item.entryTime) }}
            </div>
            <div class="font-small-2 text-muted">
              bar {{ data.item.entryIndex }}
            </div>
          </template>
          <template #cell(entryPrice)="data">
            <span class="font-small-3">{{ formatPrice(data.item.entryPrice) }}</span>
          </template>
          <template #cell(exitTime)="data">
            <div
              v-if="data.item.exitTime"
              class="font-small-3"
            >
              {{ formatDateTime(data.item.exitTime) }}
            </div>
            <div
              v-if="data.item.exitIndex !== null"
              class="font-small-2 text-muted"
            >
              bar {{ data.item.exitIndex }}
            </div>
            <div
              v-if="!data.item.exitTime"
              class="font-small-3 text-warning"
            >
              <feather-icon icon="LoaderIcon" /> Açık
            </div>
          </template>
          <template #cell(exitPrice)="data">
            <span
              v-if="data.item.exitPrice !== null"
              class="font-small-3"
            >{{ formatPrice(data.item.exitPrice) }}</span>
            <span
              v-else
              class="text-muted"
            >—</span>
          </template>
          <template #cell(duration)="data">
            <span
              v-if="data.item.durationBars !== null"
              class="font-small-3"
            >{{ formatDuration(data.item.durationBars, detailModal.item ? detailModal.item.timeframe : '') }}</span>
            <span
              v-else
              class="text-muted"
            >—</span>
          </template>
          <template #cell(profit)="data">
            <span
              v-if="data.item.profit !== null"
              :class="data.item.profit >= 0 ? 'text-success' : 'text-danger'"
              class="font-weight-bold"
            >
              {{ data.item.profit >= 0 ? '+' : '' }}{{ data.item.profit.toFixed(3) }}%
            </span>
            <span
              v-else
              class="text-muted"
            >—</span>
          </template>
          <template #cell(reason)="data">
            <div
              class="font-small-3 text-muted"
              style="max-width:300px;"
            >
              {{ data.item.exitReason || data.item.entryReason || '-' }}
            </div>
          </template>
        </b-table>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BCardHeader, BCardBody, BButton, BSpinner, BBadge, BAlert,
  BFormInput, BFormSelect, BFormCheckbox, BRow, BCol, BCollapse,
  BModal, BTable,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'QuickGainIndex',
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BButton,
    BSpinner,
    BBadge,
    BAlert,
    BFormInput,
    BFormSelect,
    BFormCheckbox,
    BRow,
    BCol,
    BCollapse,
    BModal,
    BTable,
  },
  data() {
    return {
      advancedOpen: false,
      activatedIds: [],
      showOpenTrades: true,
      detailModal: { show: false, item: null },
      tradeFields: [
        { key: 'idx', label: '#', thClass: 'width-50' },
        { key: 'side', label: 'Yön', thClass: 'width-80' },
        { key: 'entryTime', label: 'Giriş', thClass: 'width-150' },
        { key: 'entryPrice', label: 'Giriş Fiyat', thClass: 'width-120' },
        { key: 'exitTime', label: 'Çıkış', thClass: 'width-150' },
        { key: 'exitPrice', label: 'Çıkış Fiyat', thClass: 'width-120' },
        { key: 'duration', label: 'Süre', thClass: 'width-100' },
        { key: 'profit', label: 'K/Z', thClass: 'width-100' },
        { key: 'reason', label: 'Sebep' },
      ],
      form: {
        market: 'FUTURES',
        tpPerc: 0.5,
        slPerc: 0.3,
        leverage: 10,
        commissionPerSide: 0.05,
        tfsRaw: '5m,15m,1h',
        coinsRaw: '',
        selectedIndicatorIds: [],
        topNCoinsFromDb: 20,
        topNResults: 50,
        minTrades: 5,
        minNetEv: 0.02,
        minWinRate: 0,
        candleLimit: 1500,
        requireRecentSuccess: false,
        recentTradeCount: 3,
        prioritizeRecent: false,
        tpSlTuningMode: 'SUGGEST',
        quickGainOpportunityFallback: {
          mode: 'LOW_RISK_AUTO',
          minStrictActiveEdges: 3,
          fallbackMinTrades: 5,
          fallbackMinNetEv: 0.02,
          fallbackRecentWindow: 3,
          fallbackRecentMinTp: 2,
          requireLastTp: false,
          preferLastTp: true,
          maxCandidates: 10,
          maxProbationActive: 3,
          allocationMultiplier: 0.35,
          maxConcurrentProbationPositions: 2,
        },
        quickGainPoolGear: {
          mode: 'OFF',
          preset: 'APPROVED_3_SPEED',
          startGear: 1,
          lookbackTrades: 20,
          minTrades: 10,
          lossThresholdUsdt: -200,
          maxWinRate: 45,
          upProfitThresholdUsdt: 200,
          upMinWinRate: 55,
          cooldownMinutes: 60,
        },
      },
      triggeringRescan: false,
      applyingTpSl: false,
      tpSlApplyLifecycle: null,
      rescanStatusPollTimer: null,
      marketOptions: [
        { value: 'FUTURES', text: 'Futures' },
        { value: 'SPOT', text: 'Spot' },
      ],
      tpSlTuningOptions: [
        { value: 'OFF', text: 'Kapalı' },
        { value: 'SUGGEST', text: 'Öneri göster (varsayılan)' },
        { value: 'AUTO', text: 'Güvenli koşullarda otomatik uygula' },
      ],
      opportunityFallbackOptions: [
        { value: 'OFF', text: 'Kapalı' },
        { value: 'WATCH_ONLY', text: 'Sadece izle' },
        { value: 'LOW_RISK_AUTO', text: 'Düşük riskle otomatik' },
        { value: 'FULL_AUTO', text: 'Tam otomatik' },
      ],
      poolGearOptions: [
        { value: 'OFF', text: 'Kapalı' },
        { value: 'APPROVED_3_SPEED', text: 'Approved 3Gear' },
      ],
      poolGearPresetFields: [
        { key: 'gear', label: 'Vites' },
        { key: 'maxEdges', label: 'Aktif Edge' },
        { key: 'minScoreFinal', label: 'Min Skor' },
        { key: 'minWinRate', label: 'Min WR' },
        { key: 'minNetEv', label: 'Min EV' },
        { key: 'minTrades', label: 'Min Trade' },
        { key: 'symbolCooldownMinutes', label: 'Coin CD' },
        { key: 'rejectLastExitSl', label: 'Son SL Red' },
      ],
      poolGearPresetRows: [
        {
          gear: 1,
          label: 'Soft',
          maxEdges: 30,
          minScoreFinal: 0.05,
          minWinRate: 47,
          minNetEv: 0,
          minTrades: 4,
          symbolCooldownMinutes: 7,
          rejectLastExitSl: false,
        },
        {
          gear: 2,
          label: 'Relaxed',
          maxEdges: 30,
          minScoreFinal: 0,
          minWinRate: 45,
          minNetEv: 0,
          minTrades: 3,
          symbolCooldownMinutes: 5,
          rejectLastExitSl: false,
        },
        {
          gear: 3,
          label: 'Zero',
          maxEdges: 40,
          minScoreFinal: -1000,
          minWinRate: 0,
          minNetEv: -1000,
          minTrades: 0,
          symbolCooldownMinutes: 0,
          rejectLastExitSl: false,
        },
      ],
    }
  },
  computed: {
    result() { return this.$store.getters['quickGain/result'] },
    loading() { return this.$store.getters['quickGain/loading'] },
    topResults() { return this.$store.getters['quickGain/topResults'] },
    config() { return this.$store.getters['quickGain/config'] },
    summary() { return this.$store.getters['quickGain/summary'] },
    rescanDefaults() { return this.$store.getters['quickGain/rescanDefaults'] },
    detailResult() { return this.$store.getters['quickGain/detailResult'] },
    detailLoading() { return this.$store.getters['quickGain/detailLoading'] },
    saveStatus() { return this.$store.getters['quickGain/dataSaveStatus'] },
    edgeSaveStatus() { return this.$store.getters['edges/dataSaveStatus'] },
    edgeList() { return this.$store.getters['edges/dataList'] || [] },
    rescanJobs() { return this.$store.getters['rescanJobs/jobs'] || [] },
    indicatorList() { return this.$store.getters['indicators/dataList'] || [] },
    indicatorOptions() {
      return this.indicatorListForSelection
        .map(item => ({ value: item.id, text: `${item.name} (${item.engineKey})` }))
    },
    indicatorListForSelection() {
      return this.indicatorList
        .filter(item => item && item.isActive !== false && item.id)
        .slice()
        .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'tr'))
    },
    selectedIndicatorSummary() {
      const ids = this.normalizedSelectedIndicatorIds()
      if (!ids.length) return 'İndikatör seçilmedi'
      if (ids.length === this.indicatorOptions.length) return `Tüm aktif indikatörler (${ids.length})`
      return `${ids.length} indikatör seçili`
    },
    savedRescanTimeframes() {
      const timeframes = this.normalizeList(this.rescanDefaults?.timeframes)
      return timeframes.length ? timeframes.join(',') : 'TF yok'
    },
    tpSlTuning() {
      return this.rescanDefaults?.meta?.tpSlTuning || null
    },
    savedOpportunityFallback() {
      return this.normalizeOpportunityFallback(this.rescanDefaults?.meta?.quickGainOpportunityFallback)
    },
    savedPoolGear() {
      return this.normalizePoolGear(this.rescanDefaults?.meta?.quickGainPoolGear)
    },
    lastRunSummary() {
      return this.rescanDefaults?.lastRunSummary || null
    },
    lastPoolGearSummary() {
      return this.lastRunSummary?.quickGainPoolGear || null
    },
    lastFallbackSummary() {
      return this.lastRunSummary?.quickGainOpportunityFallback || null
    },
    lastFallbackApply() {
      const raw = this.lastRunSummary?.quickGainOpportunityEdges
      if (!raw || typeof raw !== 'object') return null
      return {
        ...raw,
        activated: Array.isArray(raw.activated) ? raw.activated : [],
        deactivated: Array.isArray(raw.deactivated) ? raw.deactivated : [],
        persisted: Array.isArray(raw.persisted) ? raw.persisted : [],
      }
    },
    opportunityPanelTone() {
      if (!this.lastFallbackSummary) return 'light-secondary'
      if (this.lastFallbackSummary.skipReason === 'MODE_OFF') return 'secondary'
      if (this.lastFallbackSummary.skipReason === 'STRICT_SUFFICIENT') return 'light-success'
      if (this.lastFallbackApply?.action === 'APPLIED') return 'light-warning'
      if (this.lastFallbackApply?.action === 'WATCH_ONLY') return 'light-info'
      if (this.lastFallbackSummary.triggered) return 'warning'
      return 'light-secondary'
    },
    opportunityPanelIcon() {
      if (this.lastFallbackApply?.action === 'APPLIED') return 'ZapIcon'
      if (this.lastFallbackSummary?.skipReason === 'STRICT_SUFFICIENT') return 'CheckCircleIcon'
      if (this.lastFallbackSummary?.skipReason === 'MODE_OFF') return 'PauseCircleIcon'
      return 'ActivityIcon'
    },
    opportunityPanelHeadline() {
      if (!this.lastFallbackSummary) return 'Opportunity fallback henüz değerlendirilmedi'
      if (this.lastFallbackSummary.skipReason === 'STRICT_SUFFICIENT') return 'Strict sonuç yeterli, fallback atlandı'
      if (this.lastFallbackSummary.skipReason === 'MODE_OFF') return 'Opportunity fallback kapalı'
      if (this.lastFallbackApply?.action === 'APPLIED') return 'Fallback probation edge aktifledi'
      if (this.lastFallbackApply?.action === 'WATCH_ONLY') return 'Fallback adayları izleme modunda'
      if (this.lastFallbackSummary.skipReason === 'NO_ELIGIBLE_CANDIDATE') return 'Fallback uygun aday bulamadı'
      if (this.lastFallbackSummary.triggered) return 'Fallback fırsat araması çalıştı'
      return 'Opportunity fallback beklemede'
    },
    opportunityPanelDetail() {
      const summary = this.lastFallbackSummary
      if (!summary) return 'İlk full rescan sonrası strict/fallback kararı burada görünür.'
      const parts = [
        `${summary.evaluated || 0} elenen sonuç okundu`,
        `${summary.eligible || 0} aday uygun bulundu`,
      ]
      if (summary.skipReason) parts.push(`skip: ${this.opportunitySkipReasonLabel(summary.skipReason)}`)
      if (this.lastFallbackApply) {
        parts.push(`aksiyon: ${this.opportunityApplyActionLabel(this.lastFallbackApply.action)}`)
        if (this.lastFallbackApply.reason) parts.push(this.opportunitySkipReasonLabel(this.lastFallbackApply.reason))
      }
      return parts.join(' · ')
    },
    opportunityCandidateRows() {
      const candidates = this.lastFallbackSummary?.candidates || []
      return candidates.slice(0, 5)
    },
    opportunityRuntimeRows() {
      return this.edgeList
        .map(edge => this.opportunityRuntimeRow(edge))
        .filter(Boolean)
        .slice(0, 8)
    },
    tpSlRecommendation() {
      return this.tpSlTuning?.recommendation || null
    },
    tpSlModeRecommendation() {
      return this.tpSlTuning?.modeRecommendation || this.buildLegacyTpSlModeRecommendation()
    },
    tpSlModeRecommendationLabel() {
      const mode = this.tpSlModeRecommendation?.mode
      if (mode === 'AUTO') return 'Güvenli koşullarda otomatik uygula'
      if (mode === 'SUGGEST') return 'Öneri göster'
      if (mode === 'OFF') return 'Kapalı'
      return '—'
    },
    tpSlCurrent() {
      return this.tpSlTuning?.current || null
    },
    tpSlCarrySummary() {
      return this.tpSlTuning?.signalCarry || null
    },
    tpSlScanContext() {
      return this.tpSlTuning?.scanContext || null
    },
    tpSlApplyAdvisory() {
      const advisory = this.tpSlTuning?.applyAdvisory
      if (advisory) return advisory
      return this.buildLegacyTpSlApplyAdvisory()
    },
    tpSlEligibilityText() {
      const advisory = this.tpSlApplyAdvisory
      const recommendation = this.tpSlRecommendation
      if (!recommendation) return 'Öneri yok'
      if (!advisory || advisory.level === 'CLEAR') {
        return recommendation.eligibleForAutoApply ? 'eligible' : 'manual review'
      }
      if (advisory.level === 'ELEVATED') return 'blocked'
      return 'caution'
    },
    tpSlScanContextLabel() {
      const note = this.tpSlScanContext?.note
      if (!note) return ''
      const labels = {
        outcome_supportive: 'outcome desteği güçlü',
        outcome_fragile: 'outcome desteği kırılgan',
        regime_mixed: 'rejim dağılımı karışık',
        regime_trend_up: 'trend up baskın',
        regime_trend_down: 'trend down baskın',
        regime_compression: 'compression baskın',
        regime_transition: 'transition baskın',
        regime_no_trade: 'no-trade baskın',
        indicator_outcome_missing: 'indicator outcome verisi eksik',
      }
      return labels[note] || String(note).replace(/_/g, ' ')
    },
    tpSlComparisonRows() {
      const current = this.tpSlCurrent
      const recommendation = this.tpSlRecommendation
      if (!current || !recommendation) return []
      return [
        this.makeTpSlComparisonRow('tp', 'TP %', current.tpPerc, recommendation.tpPerc, 2),
        this.makeTpSlComparisonRow('sl', 'SL %', current.slPerc, recommendation.slPerc, 2, true),
        this.makeTpSlComparisonRow('score', 'Skor', current.score, recommendation.score, 3),
        this.makeTpSlComparisonRow('ev', 'Net EV', current.netEv, recommendation.netEv, 3),
        this.makeTpSlComparisonRow('pf', 'PF', current.profitFactor, recommendation.profitFactor, 2),
        this.makeTpSlComparisonRow('dd', 'Max DD', current.maxDrawdown, recommendation.maxDrawdown, 2, true),
        this.makeTpSlComparisonRow('trades', 'İşlem', current.trades, recommendation.trades, 0),
        this.makeTpSlComparisonRow('coverage', 'Kapsam %', (current.sampleCoverage || 0) * 100, (recommendation.sampleCoverage || 0) * 100, 0),
      ]
    },
    tpSlRecommendationDrivers() {
      const current = this.tpSlCurrent
      const recommendation = this.tpSlRecommendation
      if (!current?.scoreBreakdown || !recommendation?.scoreBreakdown) return []
      const currentComponents = current.scoreBreakdown.components || {}
      const nextComponents = recommendation.scoreBreakdown.components || {}
      const labels = {
        netEv: 'EV',
        netPnl: 'Net PnL',
        profitFactor: 'PF',
        drawdown: 'DD kalitesi',
        sampleCoverage: 'Kapsam',
        confidence: 'Güven',
        tradePenalty: 'Trade penalty',
      }
      return Object.keys(labels)
        .map(key => {
          const currentValue = Number(currentComponents[key] || 0)
          const nextValue = Number(nextComponents[key] || 0)
          const invert = key === 'tradePenalty'
          const rawDelta = nextValue - currentValue
          const effectiveDelta = invert ? -rawDelta : rawDelta
          return {
            key,
            label: labels[key],
            delta: effectiveDelta,
            text: `${effectiveDelta >= 0 ? '+' : ''}${(effectiveDelta * 100).toFixed(0)}p`,
          }
        })
        .filter(item => item.delta > 0.015)
        .sort((a, b) => b.delta - a.delta)
        .slice(0, 3)
        .map(item => ({
          label: item.label,
          delta: item.text,
        }))
    },
    tpSlRecommendationWarning() {
      const warnings = this.tpSlRecommendation?.warnings || []
      if (warnings.includes('TRADE_COUNT_INFLATION_RISK')) return 'Yüksek işlem sayısı tek başına olumlu sayılmadı; kalite zayıf olduğu için öneri baskılandı.'
      if (warnings.includes('NEGATIVE_EXPECTANCY')) return 'Beklenen değer negatif; bu aday trade sayısı yüksek olsa bile korunmadı.'
      if (warnings.includes('WEAK_PROFIT_FACTOR')) return 'Profit factor zayıf; öneri kalite filtresinden baskı aldı.'
      if (warnings.includes('HIGH_DRAWDOWN_PRESSURE')) return 'Drawdown baskısı yüksek; aday korumacı şekilde aşağı çekildi.'
      return ''
    },
    tpSlApplyAdvisoryTone() {
      const level = this.tpSlApplyAdvisory?.level
      if (level === 'ELEVATED') return 'danger'
      if (level === 'CAUTION') return 'warning'
      return 'light-primary'
    },
    tpSlApplyAdvisoryHeadline() {
      const level = this.tpSlApplyAdvisory?.level
      if (level === 'ELEVATED') return 'Apply öncesi risk kontrolü tetiklendi'
      if (level === 'CAUTION') return 'Apply öncesi dikkat uyarısı'
      return 'Apply için ek risk uyarısı yok'
    },
    tpSlApplyAdvisoryDetail() {
      const advisory = this.tpSlApplyAdvisory
      if (!advisory) return ''
      const labels = advisory.codes
        .map(code => this.tpSlApplyAdvisoryCodeLabel(code))
        .filter(Boolean)
      if (!labels.length) return 'Outcome ve rejim bağlamı apply öncesi ek bir baskı göstermiyor.'
      return labels.join(' · ')
    },
    tpSlApplyAdvisoryReasonCode() {
      const codes = this.tpSlApplyAdvisory?.codes || []
      if (codes.includes('LOW_INDICATOR_COVERAGE')) return 'Low indicator coverage'
      if (codes.includes('OUTCOME_MISSING')) return 'Outcome missing'
      if (codes.includes('OUTCOME_FRAGILE')) return 'Outcome fragile'
      if (codes.includes('NEGATIVE_EXPECTANCY')) return 'Negative expectancy'
      if (codes.includes('HIGH_DRAWDOWN_PRESSURE')) return 'High drawdown pressure'
      if (codes.includes('WEAK_PROFIT_FACTOR')) return 'Weak profit factor'
      if (codes.includes('TRADE_COUNT_INFLATION_RISK')) return 'Trade count inflation'
      if (codes.includes('LOW_SAMPLE_COVERAGE')) return 'Low sample coverage'
      return ''
    },
    tpSlTuningStatus() {
      const labels = {
        READY: 'Yeni TP/SL önerisi hazır',
        KEEP_CURRENT: 'Mevcut TP/SL uygun',
        INSUFFICIENT_DATA: 'Öneri için veri yetersiz',
        AUTO_APPLIED: 'Öneri otomatik uygulandı',
        APPLIED: 'Öneri uygulandı',
      }
      return labels[this.tpSlTuning?.status] || 'İlk rescan sonrası değerlendirilir'
    },
    tpSlLifecycleJob() {
      const jobId = this.tpSlApplyLifecycle?.jobId
      if (!jobId) return null
      return this.rescanJobs.find(job => job.id === jobId) || null
    },
    tpSlLifecycleTone() {
      const status = this.tpSlLifecycleJob?.status || this.tpSlApplyLifecycle?.status || null
      if (status === 'DONE') return 'success'
      if (status === 'FAILED' || status === 'CANCELLED') return 'danger'
      if (status === 'RUNNING') return 'warning'
      return 'primary'
    },
    tpSlLifecycleHeadline() {
      if (!this.tpSlApplyLifecycle) return ''
      const status = this.tpSlLifecycleJob?.status || this.tpSlApplyLifecycle.status || 'QUEUED'
      if (status === 'QUEUED') return 'TP/SL uygulandı, rescan kuyruğa alındı'
      if (status === 'RUNNING') return 'TP/SL uygulandı, rescan şu an çalışıyor'
      if (status === 'DONE') return 'TP/SL uygulandı, rescan tamamlandı'
      if (status === 'FAILED') return 'TP/SL uygulandı, rescan hata ile kapandı'
      if (status === 'CANCEL_REQUESTED') return 'TP/SL uygulandı, rescan için iptal istendi'
      if (status === 'CANCELLED') return 'TP/SL uygulandı, rescan iptal edildi'
      return 'TP/SL uygulandı'
    },
    tpSlLifecycleDetail() {
      if (!this.tpSlApplyLifecycle) return ''
      const lifecycle = this.tpSlApplyLifecycle
      const job = this.tpSlLifecycleJob
      const parts = []
      parts.push(`Yeni baseline TP %${this.formatNum(lifecycle.tpPerc, 2)} / SL %${this.formatNum(lifecycle.slPerc, 2)}`)
      if (lifecycle.merged) parts.push('Açık bir rescan job\'ı yeniden kullanıldı')
      const status = job?.status || lifecycle.status
      if (status === 'QUEUED') parts.push(`Job ${lifecycle.jobId.slice(0, 8)} kuyrukta`)
      if (status === 'RUNNING' && job?.startedAt) parts.push(`Çalışma başladı ${this.formatDateTime(job.startedAt)}`)
      if (status === 'DONE' && job?.finishedAt) parts.push(`Tamamlandı ${this.formatDateTime(job.finishedAt)}`)
      if (status === 'FAILED') parts.push(job?.error || 'Rescan job başarısız oldu')
      return parts.join(' · ')
    },

    detailTradePairs() {
      if (!this.detailResult || !this.detailResult.signals) return []
      const pairs = []
      let openEntry = null
      this.detailResult.signals.forEach(sig => {
        if (sig.action === 'ENTRY') {
          if (openEntry) pairs.push(this.makePair(openEntry, null))
          openEntry = sig
        } else if (sig.action === 'EXIT' && openEntry) {
          pairs.push(this.makePair(openEntry, sig))
          openEntry = null
        }
      })
      if (openEntry) pairs.push(this.makePair(openEntry, null))
      return pairs.map((p, i) => ({ ...p, idx: pairs.length - i })).reverse()
    },
    visibleDetailPairs() {
      let list = this.detailTradePairs
      if (!this.showOpenTrades) list = list.filter(p => p.exitTime !== null)
      return list.slice(0, 30)
    },
  },
  watch: {
    rescanDefaults: {
      immediate: true,
      handler(val) {
        const mode = val?.meta?.tpSlTuning?.mode
        if (mode) this.form.tpSlTuningMode = mode
        if (val?.meta?.quickGainOpportunityFallback) {
          this.form.quickGainOpportunityFallback = this.normalizeOpportunityFallback(val.meta.quickGainOpportunityFallback)
        }
        if (val?.meta?.quickGainPoolGear) {
          this.form.quickGainPoolGear = this.normalizePoolGear(val.meta.quickGainPoolGear)
        }
        if ((val && val.enabled) || this.tpSlApplyLifecycle) this.startRescanStatusPolling()
        else this.stopRescanStatusPolling()
      },
    },
    tpSlLifecycleJob: {
      deep: true,
      handler(job) {
        const status = job?.status
        if (!status) return
        if (['DONE', 'FAILED', 'CANCELLED'].includes(status) && !this.rescanDefaults?.enabled) {
          this.stopRescanStatusPolling()
        }
      },
    },
    saveStatus(val) {
      if (val.status === true) {
        this.showToast({
          variant: 'success', title: 'Tamam', icon: 'CheckIcon', text: val.message,
        })
      } else if (val.status === false) {
        this.showToast({
          variant: 'danger', title: 'Hata', icon: 'XIcon', text: val.message,
        })
      }
    },
    edgeSaveStatus(val) {
      if (val.status === true) {
        this.showToast({
          variant: 'success', title: 'Tamam', icon: 'CheckIcon', text: val.message,
        })
      } else if (val.status === false) {
        this.showToast({
          variant: 'danger', title: 'Hata', icon: 'XIcon', text: val.message,
        })
      }
    },
  },
  created() {
    // Sayfa açılınca kayıtlı auto-rescan config'ini yükle + indikatör kapsamını çek
    this.$store.dispatch('quickGain/getRescanDefaults', 'QUICK_GAIN')
    this.$store.dispatch('indicators/getActive')
    this.$store.dispatch('indicators/getDataList', { activeOnly: true })
      .then(() => this.ensureDefaultSelectedIndicators())
    this.fetchOpportunityEdges()
    this.$store.dispatch('rescanJobs/fetch', { limit: 20, silent: true }).catch(() => {})
  },
  beforeDestroy() {
    this.stopRescanStatusPolling()
  },
  methods: {
    parseCsv(str) {
      if (!str) return []
      return str.split(',').map(s => s.trim().toUpperCase()).filter(Boolean)
    },
    normalizeList(value) {
      if (!value) return []
      if (Array.isArray(value)) return value.map(item => String(item).trim()).filter(Boolean)
      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value)
          if (Array.isArray(parsed)) return parsed.map(item => String(item).trim()).filter(Boolean)
        } catch (err) {
          return value.split(',').map(item => item.trim()).filter(Boolean)
        }
      }
      return []
    },
    numberOr(value, fallback) {
      const n = Number(value)
      return Number.isFinite(n) ? n : fallback
    },
    normalizeOpportunityFallback(value) {
      const raw = value && typeof value === 'object' ? value : {}
      return {
        mode: ['OFF', 'WATCH_ONLY', 'LOW_RISK_AUTO', 'FULL_AUTO'].includes(raw.mode) ? raw.mode : 'LOW_RISK_AUTO',
        minStrictActiveEdges: this.numberOr(raw.minStrictActiveEdges, 3),
        fallbackMinTrades: this.numberOr(raw.fallbackMinTrades, 5),
        fallbackMinNetEv: this.numberOr(raw.fallbackMinNetEv, 0.02),
        fallbackRecentWindow: this.numberOr(raw.fallbackRecentWindow, 3),
        fallbackRecentMinTp: this.numberOr(raw.fallbackRecentMinTp, 2),
        requireLastTp: raw.requireLastTp === true,
        preferLastTp: raw.preferLastTp !== false,
        maxCandidates: this.numberOr(raw.maxCandidates, 10),
        maxProbationActive: this.numberOr(raw.maxProbationActive, 3),
        allocationMultiplier: this.numberOr(raw.allocationMultiplier, 0.35),
        maxConcurrentProbationPositions: this.numberOr(raw.maxConcurrentProbationPositions, 2),
      }
    },
    normalizePoolGear(value) {
      const raw = value && typeof value === 'object' ? value : {}
      return {
        mode: ['OFF', 'APPROVED_3_SPEED', 'CUSTOM'].includes(raw.mode) ? raw.mode : 'OFF',
        preset: 'APPROVED_3_SPEED',
        startGear: Math.max(1, Math.min(3, Math.round(this.numberOr(raw.startGear, 1)))),
        lookbackTrades: Math.max(1, Math.min(100, Math.round(this.numberOr(raw.lookbackTrades, 20)))),
        minTrades: Math.max(1, Math.min(100, Math.round(this.numberOr(raw.minTrades, 10)))),
        lossThresholdUsdt: Math.min(0, this.numberOr(raw.lossThresholdUsdt, -200)),
        maxWinRate: Math.max(0, Math.min(100, this.numberOr(raw.maxWinRate, 45))),
        upProfitThresholdUsdt: Math.max(0, this.numberOr(raw.upProfitThresholdUsdt, 200)),
        upMinWinRate: Math.max(0, Math.min(100, this.numberOr(raw.upMinWinRate, 55))),
        cooldownMinutes: Math.max(0, Math.min(1440, Math.round(this.numberOr(raw.cooldownMinutes, 60)))),
      }
    },
    poolGearModeLabel(mode) {
      const labels = {
        OFF: 'Kapalı',
        APPROVED_3_SPEED: 'Approved 3Gear',
        CUSTOM: 'Özel',
      }
      return labels[mode] || labels.OFF
    },
    opportunityFallbackModeLabel(mode) {
      const labels = {
        OFF: 'Kapalı',
        WATCH_ONLY: 'Sadece izle',
        LOW_RISK_AUTO: 'Düşük riskle otomatik',
        FULL_AUTO: 'Tam otomatik',
      }
      return labels[mode] || labels.LOW_RISK_AUTO
    },
    opportunityModeBadgeVariant(mode) {
      if (mode === 'OFF') return 'secondary'
      if (mode === 'WATCH_ONLY') return 'info'
      if (mode === 'FULL_AUTO') return 'danger'
      return 'warning'
    },
    opportunitySkipReasonLabel(reason) {
      const labels = {
        MODE_OFF: 'mod kapalı',
        STRICT_SUFFICIENT: 'strict sonuç yeterli',
        NO_ELIGIBLE_CANDIDATE: 'uygun fallback adayı yok',
        NOT_TRIGGERED: 'tetiklenmedi',
        WATCH_ONLY: 'sadece izleme',
        NO_PROBATION_ELIGIBLE_CANDIDATE: 'probation için uygun aday yok',
        OPPORTUNITY_SL: 'SL sonrası karantina',
        OPPORTUNITY_TP: 'TP sonrası skor arttı',
      }
      return labels[reason] || String(reason || '').replace(/_/g, ' ').toLowerCase()
    },
    opportunityApplyActionLabel(action) {
      const labels = {
        APPLIED: 'probation aktif',
        WATCH_ONLY: 'izleme',
        SKIPPED: 'atlanmış',
      }
      return labels[action] || String(action || '').toLowerCase()
    },
    opportunityCandidateReason(candidate) {
      const codes = candidate.reasonCodes || []
      const labels = {
        RECENT_SUCCESS_MISS: 'recent strict eşiğine takıldı',
        LOW_TRADE_COUNT: 'işlem sayısı düşük',
        LOW_NET_EV: 'EV zayıf',
        STALE_SIGNAL: 'sinyal eski',
        DRAWDOWN_PRESSURE: 'drawdown baskısı',
      }
      const text = codes.map(code => labels[code]).filter(Boolean).join(' · ')
      return text || candidate.strictRejectReason || 'yakın fırsat adayı'
    },
    opportunityRuntimeRow(edge) {
      const meta = edge?.meta || {}
      const fallback = meta.opportunityFallback || {}
      const tier = meta.opportunityTier || fallback.tier
      if (!tier) return null
      const stats = fallback.runtimeStats || {}
      const last = fallback.lastOutcome || {}
      const isQuarantined = tier === 'QUARANTINED_OPPORTUNITY'
      const isActive = edge.isActive === true
      const tp = Number(stats.tp || 0)
      const sl = Number(stats.sl || 0)
      const trades = Number(stats.trades || 0)
      const winRate = trades > 0 ? (tp / trades) * 100 : null
      const outcome = last.outcome || null
      return {
        id: edge.id,
        symbol: edge.symbol,
        timeframe: edge.timeframe,
        tier,
        tierLabel: isQuarantined ? 'quarantine' : isActive ? 'probation aktif' : 'probation pasif',
        badgeVariant: isQuarantined ? 'danger' : isActive ? 'warning' : 'secondary',
        statsLabel: trades > 0
          ? `${trades} kapanış · TP ${tp} / SL ${sl} · WR %${this.formatNum(winRate, 0)}`
          : `risk x${this.formatNum(meta.probationAllocationMultiplier || fallback.allocationMultiplier || 1, 2)}`,
        lastOutcomeLabel: outcome
          ? `son ${outcome}: ${this.formatSigned(last.realizedPnl, 2)} USDT`
          : 'henüz outcome yok',
        lastOutcomeClass: outcome === 'TP' ? 'text-success' : outcome === 'SL' ? 'text-danger' : 'text-muted',
      }
    },
    fetchOpportunityEdges() {
      return this.$store.dispatch('edges/getDataList', {
        kind: 'QUICK_GAIN',
        limit: 100,
        page: 1,
      }).catch(() => {})
    },
    normalizedSelectedIndicatorIds() {
      return Array.from(new Set((this.form.selectedIndicatorIds || [])
        .map(id => Number(id))
        .filter(id => Number.isFinite(id) && id > 0)))
    },
    activeIndicatorIds() {
      return this.indicatorList
        .filter(item => item && item.isActive !== false && item.id)
        .map(item => Number(item.id))
        .filter(id => Number.isFinite(id) && id > 0)
    },
    async ensureIndicatorList() {
      if (this.indicatorOptions.length) return
      await this.$store.dispatch('indicators/getDataList', { activeOnly: true })
    },
    ensureDefaultSelectedIndicators() {
      if (this.normalizedSelectedIndicatorIds().length) return
      this.form.selectedIndicatorIds = this.activeIndicatorIds()
    },
    async resolveSelectedIndicatorIds() {
      await this.ensureIndicatorList()
      let indicatorIds = this.normalizedSelectedIndicatorIds()
      if (!indicatorIds.length) {
        indicatorIds = this.activeIndicatorIds()
        this.form.selectedIndicatorIds = indicatorIds
      }
      return indicatorIds
    },
    selectAllIndicators() {
      this.form.selectedIndicatorIds = this.activeIndicatorIds()
    },
    clearIndicatorSelection() {
      this.form.selectedIndicatorIds = []
    },
    isIndicatorSelected(indicatorId) {
      return this.normalizedSelectedIndicatorIds().includes(Number(indicatorId))
    },
    toggleIndicatorSelection(indicatorId, checked) {
      const id = Number(indicatorId)
      if (!Number.isFinite(id) || id <= 0) return
      const current = this.normalizedSelectedIndicatorIds()
      if (checked && !current.includes(id)) {
        this.form.selectedIndicatorIds = [...current, id]
        return
      }
      if (!checked) {
        this.form.selectedIndicatorIds = current.filter(item => item !== id)
      }
    },
    formatNum(value, decimals = 2) {
      if (value === null || value === undefined) return '—'
      const n = Number(value)
      if (!Number.isFinite(n)) return '—'
      return n.toFixed(decimals)
    },
    formatSigned(value, decimals = 2) {
      const n = Number(value)
      if (!Number.isFinite(n)) return '—'
      return `${n >= 0 ? '+' : ''}${n.toFixed(decimals)}`
    },
    buildLegacyTpSlApplyAdvisory() {
      if (!this.tpSlRecommendation && !this.tpSlScanContext) return null
      const codes = []
      const note = this.tpSlScanContext?.note
      if (note === 'indicator_outcome_missing') codes.push('OUTCOME_MISSING')
      if (note === 'outcome_fragile') codes.push('OUTCOME_FRAGILE')
      if (note === 'regime_mixed') codes.push('REGIME_MIXED')

      const indicatorCount = Number(this.tpSlScanContext?.indicatorCount || 0)
      const coveredCount = Number(this.tpSlScanContext?.coveredIndicatorCount || 0)
      if (indicatorCount > 0 && (coveredCount / indicatorCount) < 0.5) codes.push('LOW_INDICATOR_COVERAGE')

      const warnings = this.tpSlRecommendation?.warnings || []
      if (warnings.includes('NEGATIVE_EXPECTANCY')) codes.push('NEGATIVE_EXPECTANCY')
      if (warnings.includes('WEAK_PROFIT_FACTOR')) codes.push('WEAK_PROFIT_FACTOR')
      if (warnings.includes('HIGH_DRAWDOWN_PRESSURE')) codes.push('HIGH_DRAWDOWN_PRESSURE')
      if (warnings.includes('TRADE_COUNT_INFLATION_RISK')) codes.push('TRADE_COUNT_INFLATION_RISK')
      if (Number(this.tpSlRecommendation?.sampleCoverage || 0) > 0 && Number(this.tpSlRecommendation?.sampleCoverage || 0) < 0.6) {
        codes.push('LOW_SAMPLE_COVERAGE')
      }

      const uniqueCodes = Array.from(new Set(codes))
      const elevatedCodes = ['OUTCOME_MISSING', 'OUTCOME_FRAGILE', 'NEGATIVE_EXPECTANCY', 'HIGH_DRAWDOWN_PRESSURE']
      let level = 'CLEAR'
      if (uniqueCodes.some(code => elevatedCodes.includes(code))) level = 'ELEVATED'
      else if (uniqueCodes.length) level = 'CAUTION'
      return {
        level,
        requiresConfirmation: level !== 'CLEAR',
        codes: uniqueCodes,
      }
    },
    buildLegacyTpSlModeRecommendation() {
      const advisory = this.tpSlApplyAdvisory
      const hasRecommendation = Boolean(this.tpSlRecommendation)
      if (!hasRecommendation) {
        return {
          mode: 'OFF',
          reason: 'Henüz uygulanabilir TP/SL önerisi yok; tuning kapalı veya manuel izlemeye uygun.',
        }
      }
      const codes = advisory?.codes || []
      const level = advisory?.level || 'CLEAR'
      if (level === 'ELEVATED' && (
        codes.includes('OUTCOME_MISSING')
        || codes.includes('OUTCOME_FRAGILE')
        || codes.includes('NEGATIVE_EXPECTANCY')
      )) {
        return {
          mode: 'OFF',
          reason: 'Outcome bağlamı zayıf; otomatik tuning yerine önce manuel gözlem tercih edilmeli.',
        }
      }
      if (level === 'CLEAR' && this.tpSlRecommendation?.eligibleForAutoApply) {
        return {
          mode: 'AUTO',
          reason: 'Outcome ve rejim bağlamı temiz; güvenli koşullarda otomatik apply kullanılabilir.',
        }
      }
      return {
        mode: 'SUGGEST',
        reason: 'Öneri göster modu daha uygun; sistem öneriyi sunsun ama apply kararı kullanıcıda kalsın.',
      }
    },
    tpSlApplyAdvisoryCodeLabel(code) {
      const labels = {
        OUTCOME_MISSING: 'indicator outcome kapsamı eksik; öneri daha zayıf bağlamla geliyor',
        OUTCOME_FRAGILE: 'indicator outcome desteği kırılgan; winner davranışı yeterince sağlam değil',
        REGIME_MIXED: 'rejim dağılımı karışık; aynı öneri farklı koşullarda tutarsız çalışabilir',
        LOW_INDICATOR_COVERAGE: 'aktif indikatörlerin yarısından azı outcome verisiyle doğrulanabildi',
        NEGATIVE_EXPECTANCY: 'adayın beklenen değeri zayıf veya negatif baskı görüyor',
        WEAK_PROFIT_FACTOR: 'profit factor tarafı yeterince rahat değil',
        HIGH_DRAWDOWN_PRESSURE: 'drawdown baskısı yüksek; apply sonrası daha sert geri verme riski var',
        TRADE_COUNT_INFLATION_RISK: 'yüksek işlem sayısı kaliteyi tek başına doğrulamıyor',
        LOW_SAMPLE_COVERAGE: 'örnek kapsamı düşük; öneri dar veri üstünden şekillenmiş olabilir',
      }
      return labels[code] || ''
    },
    onApplyTpSlModeRecommendation() {
      if (!this.tpSlModeRecommendation?.mode) return
      this.form.tpSlTuningMode = this.tpSlModeRecommendation.mode
      this.showToast({
        variant: 'success',
        title: 'Hazır',
        icon: 'CheckIcon',
        text: `TP/SL tuning modu ${this.tpSlModeRecommendationLabel} olarak ayarlandı.`,
      })
    },
    makeTpSlComparisonRow(key, label, currentValue, recommendedValue, decimals = 2, lowerIsBetter = false) {
      const current = Number(currentValue)
      const recommended = Number(recommendedValue)
      const delta = recommended - current
      const effectiveDelta = lowerIsBetter ? -delta : delta
      let variant = 'light-secondary'
      if (effectiveDelta > 0.0001) variant = 'light-success'
      else if (effectiveDelta < -0.0001) variant = 'light-danger'
      return {
        key,
        label,
        current: this.formatNum(current, decimals),
        recommended: this.formatNum(recommended, decimals),
        delta: this.formatSigned(delta, decimals),
        variant,
      }
    },
    recentBadgeVariant(item) {
      if (!item.recentTotalCount) return 'light-secondary'
      const ratio = item.recentTpCount / item.recentTotalCount
      if (ratio === 1) return 'success'
      if (ratio >= 0.66) return 'warning'
      return 'light-danger'
    },
    recencyTooltip(item) {
      const parts = []
      if (item.barsFromLastTrade !== undefined) parts.push(`Son işlem: ${item.barsFromLastTrade} bar önce`)
      if (item.volatilityRatio !== undefined) parts.push(`Volatilite oranı: ×${this.formatNum(item.volatilityRatio, 2)}`)
      if (item.recencyMul && item.recencyMul !== 1) parts.push(`Skor çarpanı: ×${this.formatNum(item.recencyMul, 2)}`)
      return parts.join(' · ')
    },

    // ─── Auto-rescan handlers ──────────────────────────────────────────────
    onEditRescanDefaults() {
      if (!this.rescanDefaults) return
      this.applyRescanDefaultsToForm(this.rescanDefaults)
      this.advancedOpen = true
      this.showToast({
        variant: 'info',
        title: 'Düzenleme modu',
        icon: 'Edit2Icon',
        text: 'Kayıtlı Quick Scan ayarları forma yüklendi. Değiştirip tekrar kaydedebilirsin.',
      })
    },
    applyRescanDefaultsToForm(defaults) {
      const coins = this.normalizeList(defaults.coins)
      const timeframes = this.normalizeList(defaults.timeframes)
      const indicatorIds = this.normalizeList(defaults.indicatorIds)
        .map(id => Number(id))
        .filter(id => Number.isFinite(id) && id > 0)
      const tuningMode = defaults.meta?.tpSlTuning?.mode
      const opportunityFallback = this.normalizeOpportunityFallback(defaults.meta?.quickGainOpportunityFallback)
      const poolGear = this.normalizePoolGear(defaults.meta?.quickGainPoolGear)
      this.form.market = defaults.market || this.form.market
      this.form.coinsRaw = coins.join(',')
      this.form.tfsRaw = timeframes.length ? timeframes.join(',') : this.form.tfsRaw
      this.form.selectedIndicatorIds = indicatorIds.length ? indicatorIds : this.form.selectedIndicatorIds
      this.form.topNCoinsFromDb = this.numberOr(defaults.topNCoinsFromDb, this.form.topNCoinsFromDb)
      this.form.topNResults = this.numberOr(defaults.topNResults, this.form.topNResults)
      this.form.candleLimit = this.numberOr(defaults.candleLimit, this.form.candleLimit)
      this.form.tpPerc = this.numberOr(defaults.tpPerc, this.form.tpPerc)
      this.form.slPerc = this.numberOr(defaults.slPerc, this.form.slPerc)
      this.form.leverage = this.numberOr(defaults.leverage, this.form.leverage)
      this.form.commissionPerSide = this.numberOr(defaults.commissionPerSide, this.form.commissionPerSide)
      this.form.minTrades = this.numberOr(defaults.minTrades, this.form.minTrades)
      this.form.minNetEv = this.numberOr(defaults.minNetEv, this.form.minNetEv)
      this.form.minWinRate = this.numberOr(defaults.minWinRate, this.form.minWinRate)
      this.form.requireRecentSuccess = defaults.requireRecentSuccess === true
      this.form.recentTradeCount = this.numberOr(defaults.recentTradeCount, this.form.recentTradeCount)
      this.form.prioritizeRecent = defaults.prioritizeRecent === true
      if (['OFF', 'SUGGEST', 'AUTO'].includes(tuningMode)) this.form.tpSlTuningMode = tuningMode
      this.form.quickGainOpportunityFallback = opportunityFallback
      this.form.quickGainPoolGear = poolGear
    },
    async onSaveAutoRescan() {
      const timeframes = this.parseCsv(this.form.tfsRaw).map(s => s.toLowerCase())
      const coins = this.parseCsv(this.form.coinsRaw)
      const indicatorIds = await this.resolveSelectedIndicatorIds()

      if (!indicatorIds.length) {
        this.showToast({
          variant: 'danger', title: 'Eksik', icon: 'XIcon', text: 'En az 1 indikatör seçmelisin.',
        })
        return
      }
      if (!timeframes.length) {
        this.showToast({
          variant: 'danger', title: 'Eksik', icon: 'XIcon', text: 'En az 1 timeframe gerekli.',
        })
        return
      }
      const updating = Boolean(this.rescanDefaults)
      this.$swal({
        title: updating ? 'Kayıtlı Quick Scan güncellensin mi?' : 'Otomatik taramaya kaydedilsin mi?',
        text: updating
          ? 'Mevcut kayıt bu parametrelerle güncellenir. Aktif/pasif durumu korunur.'
          : 'Bu parametrelerle saatlik scan otomatik çalışır. Sonra "Aktive Et" ile başlatılır.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: updating ? 'Güncelle' : 'Kaydet',
        cancelButtonText: 'İptal',
        customClass: { confirmButton: 'btn btn-primary', cancelButton: 'btn btn-secondary ml-1' },
        buttonsStyling: false,
      }).then(async result => {
        if (!result.value) return
        const body = {
          kind: 'QUICK_GAIN',
          enabled: this.rescanDefaults ? this.rescanDefaults.enabled === true : false,
          market: this.form.market,
          coins: coins.length ? coins : [], // boş ise backend top-N DB'den çeker
          timeframes,
          indicatorIds,
          topNResults: this.form.topNResults,
          topNCoinsFromDb: this.form.topNCoinsFromDb,
          candleLimit: this.form.candleLimit,
          tpPerc: this.form.tpPerc,
          slPerc: this.form.slPerc,
          leverage: this.form.leverage,
          commissionPerSide: this.form.commissionPerSide,
          minTrades: this.form.minTrades,
          minNetEv: this.form.minNetEv,
          minWinRate: this.form.minWinRate,
          requireRecentSuccess: this.form.requireRecentSuccess,
          recentTradeCount: this.form.recentTradeCount,
          prioritizeRecent: this.form.prioritizeRecent,
          tpSlTuningMode: this.form.tpSlTuningMode,
          quickGainOpportunityFallback: this.normalizeOpportunityFallback(this.form.quickGainOpportunityFallback),
          quickGainPoolGear: this.normalizePoolGear(this.form.quickGainPoolGear),
        }
        // Boş coin listesinde scheduler topNCoinsFromDb'den, legacy tek indikatörde DB aktif indikatörlerden resolve eder
        await this.$store.dispatch('quickGain/saveRescanDefaults', body)
      })
    },
    async onToggleRescan() {
      if (!this.rescanDefaults) return
      const newState = !this.rescanDefaults.enabled
      await this.$store.dispatch('quickGain/toggleRescanEnabled', {
        kind: 'QUICK_GAIN',
        enabled: newState,
      })
    },
    async onTriggerRescan() {
      if (!this.rescanDefaults || !this.rescanDefaults.enabled) return
      this.triggeringRescan = true
      try {
        await this.$store.dispatch('quickGain/triggerRescan', 'QUICK_GAIN')
        this.startRescanStatusPolling()
      } finally {
        this.triggeringRescan = false
      }
    },
    async onApplyTpSlRecommendation() {
      if (!this.tpSlRecommendation) return
      const advisory = this.tpSlApplyAdvisory
      const baseText = `Yeni TP %${this.tpSlRecommendation.tpPerc}, SL %${this.tpSlRecommendation.slPerc}. Açık pozisyonlar değişmez; yeni tarama başlatılır.`
      const advisoryDetail = advisory?.codes?.length ? this.tpSlApplyAdvisoryDetail : ''
      const result = await this.$swal({
        title: advisory?.requiresConfirmation ? 'TP/SL önerisi yine de uygulansın mı?' : 'TP/SL önerisi uygulansın mı?',
        text: advisoryDetail ? `${baseText}\n\nRisk notu: ${advisoryDetail}` : baseText,
        icon: advisory?.requiresConfirmation ? 'warning' : 'question',
        showCancelButton: true,
        confirmButtonText: advisory?.requiresConfirmation ? 'Yine de uygula' : 'Uygula',
        cancelButtonText: 'İptal',
        customClass: { confirmButton: 'btn btn-primary', cancelButton: 'btn btn-secondary ml-1' },
        buttonsStyling: false,
      })
      if (!result.value) return
      this.applyingTpSl = true
      try {
        const response = await this.$store.dispatch('quickGain/applyTpSlRecommendation', 'QUICK_GAIN')
        const defaults = response && response.defaults
        const job = response && response.job
        if (defaults) {
          this.form.tpPerc = Number(defaults.tpPerc)
          this.form.slPerc = Number(defaults.slPerc)
        }
        if (job?.id) {
          this.tpSlApplyLifecycle = {
            jobId: job.id,
            status: job.status || 'QUEUED',
            merged: job.merged === true,
            source: job.source || null,
            tpPerc: defaults ? Number(defaults.tpPerc) : Number(this.tpSlRecommendation.tpPerc),
            slPerc: defaults ? Number(defaults.slPerc) : Number(this.tpSlRecommendation.slPerc),
          }
        }
        await this.$store.dispatch('rescanJobs/fetch', { limit: 20, silent: true }).catch(() => {})
        this.startRescanStatusPolling()
      } finally {
        this.applyingTpSl = false
      }
    },
    onShowRescanStatus() {
      this.$store.dispatch('quickGain/getRescanDefaults', 'QUICK_GAIN')
      this.fetchOpportunityEdges()
      this.$store.dispatch('rescanJobs/fetch', { limit: 20, silent: true }).catch(() => {})
      if (this.rescanDefaults && this.rescanDefaults.enabled) this.startRescanStatusPolling()
    },
    startRescanStatusPolling() {
      if (this.rescanStatusPollTimer) return
      this.rescanStatusPollTimer = setInterval(() => {
        this.$store.dispatch('quickGain/getRescanDefaults', 'QUICK_GAIN')
        this.fetchOpportunityEdges()
        if (this.tpSlApplyLifecycle || this.rescanDefaults?.enabled) {
          this.$store.dispatch('rescanJobs/fetch', { limit: 20, silent: true }).catch(() => {})
        }
      }, 5000)
    },
    stopRescanStatusPolling() {
      if (!this.rescanStatusPollTimer) return
      clearInterval(this.rescanStatusPollTimer)
      this.rescanStatusPollTimer = null
    },
    async onRemoveRescan() {
      this.$swal({
        title: 'Auto-rescan silinsin mi?',
        text: 'Kayıt geri yüklenemez.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sil',
        cancelButtonText: 'İptal',
        customClass: { confirmButton: 'btn btn-danger', cancelButton: 'btn btn-secondary ml-1' },
        buttonsStyling: false,
      }).then(async result => {
        if (result.value) {
          await this.$store.dispatch('quickGain/removeRescanDefaults', 'QUICK_GAIN')
        }
      })
    },

    // ─── Detail modal handlers ─────────────────────────────────────────────
    onShowDetail(item) {
      this.detailModal.show = true
      this.detailModal.item = item
      this.$store.dispatch('quickGain/loadDetail', item)
    },
    onCloseDetail() {
      this.detailModal.show = false
      this.detailModal.item = null
      this.$store.dispatch('quickGain/clearDetail')
    },

    // ─── Trade pair + formatter helpers (Detail modal için) ───────────────
    makePair(entry, exit) {
      return {
        side: entry.type,
        entryIndex: entry.index,
        entryTime: entry.timestamp,
        entryPrice: entry.price,
        entryReason: entry.reason,
        exitIndex: exit ? exit.index : null,
        exitTime: exit ? exit.timestamp : null,
        exitPrice: exit ? exit.price : null,
        exitReason: exit ? exit.reason : null,
        durationBars: exit ? exit.index - entry.index : null,
        profit: exit ? (exit.profit ?? null) : null,
      }
    },
    formatDateTime(value) {
      if (!value) return '—'
      try {
        const d = new Date(value)
        if (Number.isNaN(d.getTime())) return String(value)
        return d.toLocaleString('tr-TR', {
          day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
        })
      } catch (e) { return String(value) }
    },
    formatPrice(value) {
      if (value === null || value === undefined) return '—'
      const n = Number(value)
      if (!Number.isFinite(n)) return '—'
      let decimals = 4
      if (n >= 1000) decimals = 2
      else if (n < 1) decimals = 6
      return n.toLocaleString('tr-TR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
    },
    formatDuration(bars, tf) {
      if (bars === null || bars === undefined) return '—'
      const tfMins = {
        '1m': 1,
        '3m': 3,
        '5m': 5,
        '15m': 15,
        '30m': 30,
        '1h': 60,
        '2h': 120,
        '4h': 240,
        '1d': 1440,
      }
      const min = bars * (tfMins[tf] || 0)
      if (!min) return `${bars} bar`
      if (min < 60) return `${bars}b · ${min}dk`
      if (min < 1440) return `${bars}b · ${(min / 60).toFixed(1)}sa`
      return `${bars}b · ${(min / 1440).toFixed(1)}gün`
    },
    async onRun() {
      this.activatedIds = []
      const timeframes = this.parseCsv(this.form.tfsRaw).map(s => s.toLowerCase())
      const coins = this.parseCsv(this.form.coinsRaw)
      const indicatorIds = await this.resolveSelectedIndicatorIds()

      if (!indicatorIds.length) {
        this.showToast({
          variant: 'danger', title: 'Eksik', icon: 'XIcon', text: 'En az 1 indikatör seçmelisin.',
        })
        return
      }

      const body = {
        market: this.form.market,
        tpPerc: this.form.tpPerc,
        slPerc: this.form.slPerc,
        leverage: this.form.leverage,
        commissionPerSide: this.form.commissionPerSide,
        candleLimit: this.form.candleLimit,
        topNResults: this.form.topNResults,
        prioritizeRecent: this.form.prioritizeRecent,
        indicatorIds,
      }
      if (timeframes.length) body.timeframes = timeframes
      if (coins.length) body.coins = coins
      if (this.form.topNCoinsFromDb) body.topNCoinsFromDb = this.form.topNCoinsFromDb
      body.filters = {
        minTrades: this.form.minTrades,
        minNetEv: this.form.minNetEv,
        minWinRate: this.form.minWinRate,
        requireRecentSuccess: this.form.requireRecentSuccess,
        recentTradeCount: this.form.recentTradeCount,
      }
      await this.$store.dispatch('quickGain/runScan', body)
    },
    async onActivate(item) {
      if (!item.edgeId) {
        this.showToast({
          variant: 'warning', title: 'Edge ID yok', icon: 'AlertCircleIcon', text: 'Bu öneri henüz Library\'e kayıt edilmemiş.',
        })
        return
      }
      await this.$store.dispatch('edges/toggleActive', { id: item.edgeId, isActive: true })
      this.activatedIds.push(item.edgeId)
    },
    showToast(params) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: params.title, icon: params.icon, text: params.text, variant: params.variant,
        },
      })
    },
  },
}
</script>

<style scoped>
.border-success-light {
  border: 1px solid rgba(40, 199, 111, 0.3) !important;
}
.border-right {
  border-right: 1px solid rgba(0, 0, 0, 0.08);
}
.tp-sl-compare-panel {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(115, 103, 240, 0.12);
}
.tp-sl-compare-header,
.tp-sl-compare-row {
  display: grid;
  grid-template-columns: minmax(84px, 1.2fr) minmax(58px, 0.8fr) minmax(58px, 0.8fr) minmax(56px, 0.6fr);
  gap: 0.5rem;
  align-items: center;
}
.tp-sl-compare-header {
  margin-bottom: 0.35rem;
  font-size: 0.72rem;
  color: #6e6b7b;
}
.tp-sl-compare-row {
  padding: 0.22rem 0;
  font-size: 0.84rem;
}
.tp-sl-driver-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
  margin-top: 0.55rem;
}
.tp-sl-driver-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.16rem 0.45rem;
  border-radius: 6px;
  background: rgba(115, 103, 240, 0.08);
  color: #5e50ee;
  font-size: 0.74rem;
  font-weight: 500;
}
.pool-gear-preset-panel {
  padding-top: 0.75rem;
  border-top: 1px solid rgba(115, 103, 240, 0.12);
}
.quick-gain-compact-table {
  font-size: 0.78rem;
}
@media (max-width: 767.98px) {
  .tp-sl-compare-header {
    display: none;
  }
  .tp-sl-compare-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.3rem 0.6rem;
    padding: 0.4rem 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  }
}
</style>
